import { Avatar, Tooltip } from "@mui/material";
import React, { useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";

import SelectPrimaryContactModal from "@/Components/modals/client/UserAccessModal/SelectPrimaryContactModal";
import {
  fetchClientUsers,
  updateToggleSelected,
  updateSelectedClientUser,
  toggleSelectedAll,
  toggleAddNewUserModal,
  updateSorting,
  deSelectAllUsers,
  updatePagination,
  setClientUsersLoading,
} from "@/reducers/client_user/clientUsers";
import { TOOLTIPTEXT } from "@/utilities/CommonText";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { showErrorToast } from "@/utilities/utils";

import Actions from "./Actions";
import FilterModal from "./FilterModal";
import SearchFilter from "./SearchFilter";
import { apiDecrypt } from "../../../../../utilities/keyEncryption";
import DataTablePaginationLoader from "../../../../Skeletons/DataTablePaginationLoader";
import TableLoader from "../../../TableLoader";
import TableNoContent from "../../../TableNoContent";
import { SelectedCountText } from "../../candidates/Listing/common/SelectedCountText";
const UsersList = () => {
  const {
    isLoading,
    clientUsers,
    selectedClientUser,
    totalCount,
    pagination,
    isSelectedAll,
    toggleSelected,
    isPaginationLoading,
    sorting,
  } = useSelector((state) => state.clientUsers);
  const dispatch = useDispatch();
  const dataTableRef = useRef();
  const { user } = useSelector((state) => state.clientProfile);

  useEffect(() => {
    if (selectedClientUser.length == 0) {
      dispatch(updateToggleSelected());
    }
  }, [selectedClientUser]);

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchClientUsers({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const handleRowClickAlert = (row) => {
    if (String(row.user_id) === String(user.id)) {
      showErrorToast(
        "Action not allowed: You cannot edit or delete your own account.",
        {
          id: "no-own-selected",
        },
      );
    } else if (row.role === "Agency Admin") {
      showErrorToast(
        "Action not allowed: The Agency Admin account cannot be modified.",
        {
          id: "no-admin-selected",
        },
      );
    }
  };

  const columns = [
    {
      name: "User",
      sortable: true,
      sortField: "contact_forename",
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "client_user_name"),
      selector: (row) => (
        <div className="flex gap-[14px]">
          <div className="min-w-[45px]">
            <Avatar
              alt={row.client_user_name
                .trim()
                .split(" ")[0]
                .charAt(0)
                .toUpperCase()}
              sx={{ width: 45, height: 45 }}
              src={row.profile_image ? row.profile_image : ""}
            />
          </div>
          <div className="grid grow items-center justify-center gap-1 overflow-hidden">
            <Tooltip
              title={
                !user.is_primary_contact &&
                apiDecrypt(row?.client_user_id) === user.id
                  ? TOOLTIPTEXT.checkOwnUser
                  : ""
              }
              placement="top"
              arrow
            >
              <span className="truncate text-[14px] font-semibold capitalize leading-[normal] text-black">
                {row.client_user_name}
              </span>
            </Tooltip>
            {row.is_primary ? (
              <Tooltip
                title={
                  row.is_primary && !user.is_primary_contact
                    ? TOOLTIPTEXT.checkPrimaryContact
                    : ""
                }
                placement="top"
                arrow
              >
                <span className="w-fit truncate rounded-md bg-lightblue px-4 py-1 text-[12px] font-semibold capitalize leading-[normal] text-blue">
                  {row.is_primary ? "Primary" : ""}
                </span>
              </Tooltip>
            ) : null}
          </div>
        </div>
      ),
      width: "25%",
    },
    // 2
    {
      name: "Role",
      sortable: true,
      sortField: "role_name",
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "role"),
      selector: (row) => (
        <div className="flex flex-col">
          <span className="line-clamp-1 text-[14px] font-semibold capitalize leading-[normal] text-black">
            {row.role}
          </span>
        </div>
      ),
      width: "20%",
    },
    {
      name: "Email Address",
      sortable: true,
      sortField: "contact_email",
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "user_email"),

      /**
       * A function that takes a row as input and returns a JSX element.
       *
       * @param {object} row - The row object
       * @return {JSX.Element} The JSX element representing the cell
       */
      selector: (row) => (
        <div className="flex flex-col">
          <div className="flex flex-col" data-tag="allowRowEvents">
            {/* {getColumnNormalHtml(row.phone_number, "truncate")} */}
            <span className="truncate text-[14px] font-medium leading-[normal] text-black">
              {row.user_email}
            </span>
          </div>
        </div>
      ),
      width: "25%",
    },
    {
      name: "Phone Number",
      sortable: true,
      sortField: "contact_mobile",
      // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "phone_number"),
      selector: (row) => (
        <div className="flex flex-col">
          <div className="flex flex-col" data-tag="allowRowEvents">
            <span
              className={`truncate !text-center text-[14px] font-medium leading-[normal] text-black`}
            >
              {row.phone_number || "-"}
            </span>
          </div>
        </div>
      ),
      width: "14.5%",
    },
    {
      name: "Direct Dial",
      sortable: true,
      sortField: "contact_telno",
      // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "telephone_no"),

      /**
       * A function that takes a row as input and returns a JSX element.
       */
      selector: (row) => (
        <div className="flex flex-col">
          <div className="flex flex-col" data-tag="allowRowEvents">
            <span
              className={`truncate !text-center text-[14px] font-medium leading-[normal] text-black`}
            >
              {row.direct_dial || "-"}
            </span>
          </div>
        </div>
      ),
      width: "12.5%",
    },
  ];

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    dispatch(setClientUsersLoading(true));
    dispatch(deSelectAllUsers());
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(fetchClientUsers());
  };

  return (
    <div className="UsersList_Wrapper mb-[52px]">
      <SelectPrimaryContactModal />
      <div className="flex h-full flex-col rounded-[10px] bg-white p-4 shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="mb-[20px] flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[11px] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-[21px]">
            <h4 className="text-[20px] font-medium leading-[normal] text-black">
              Users
            </h4>
            <Actions />
            <SelectedCountText
              selectedCount={selectedClientUser.length}
              totalCount={totalCount}
              isSelectedAll={isSelectedAll}
              clearSelected={() => {
                dispatch(updateToggleSelected());
              }}
              handleSelectAll={(val) => {
                dispatch(toggleSelectedAll(val));
              }}
              updateSelected={(val) => {
                dispatch(updateSelectedClientUser(val));
              }}
              currentDataLength={clientUsers.length}
            />
          </div>
          <div className="flex gap-[20px]">
            {/* {
              user.is_primary_contact ? (
                <Tooltip title={TOOLTIPTEXT.primaryContact} placement="top" arrow>
                  <button
                    className={`tour-client-user-set-primary transition-linear flex h-[40px] focus:outline-none items-center gap-[6px] rounded-lg border border-primary bg-lightblue px-4 font-semibold leading-[normal] text-primary hover:bg-primary hover:text-white `}
                    onClick={() => {
                      dispatch(togglePrimaryModal(true))
                    }}
                  >
                    <ActionIcons type={"contactPrimary"} />
                  </button>
                </Tooltip>
              ) : null
            } */}
            <div className="flex gap-4">
              <button
                className="transition-linear tour-client-user-add-user h-[40px] rounded-lg border border-primary bg-lightblue px-4 font-semibold leading-[normal] text-primary hover:bg-primary hover:text-white focus:outline-none"
                onClick={() => {
                  dispatch(toggleAddNewUserModal(true));
                }}
              >
                Add New User
              </button>
            </div>
            <SearchFilter />
            <FilterModal />
          </div>
        </div>
        <div
          className="DataTable_Wrapper hide_extended_arrow"
          ref={dataTableRef}
        >
          <DataTable
            columns={columns}
            data={clientUsers}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            onSort={handleSort}
            // persistTableHead={true}
            progressPending={isLoading}
            progressComponent={<TableLoader rows={5} />}
            noDataComponent={<TableNoContent />}
            selectableRows
            selectableRowDisabled={(row) =>
              String(row.user_id) === String(user.id) ||
              row.role === "Agency Admin"
            }
            onSelectedRowsChange={(state) => {
              const selectedIDS = state.selectedRows.map(
                (item) => item.user_id,
              );
              dispatch(updateSelectedClientUser(selectedIDS));
            }}
            //prevent multtiple selection of same user alert
            onRowClicked={handleRowClickAlert}
            clearSelectedRows={toggleSelected}
            className="_DataTable"
            conditionalRowStyles={[
              {
                when: (row) =>
                  String(row.user_id) === String(user.id) ||
                  row.role === "Agency Admin",
                style: {
                  cursor: "not-allowed",
                  opacity: 0.7,
                },
              },
            ]}
            customStyles={{
              rows: {
                style: {
                  '& .rdt_TableCell input[type="checkbox"]:disabled ': {
                    backgroundColor: "txtDark !important",
                    border: "2px solid lightGrey !important", // Applies the grey border for admin's or own row checkbox
                  },
                },
              },
            }}
          />
        </div>
        {!isLoading && isPaginationLoading ? (
          <DataTablePaginationLoader />
        ) : null}
      </div>
    </div>
  );
};

export default UsersList;
